import { Injectable } from '@angular/core';
import { SecureStorageService } from './secure-storage.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private permissions: any[] = [];

  get isSuperAdmin(): boolean {
    const user = this.secureStorage.getObject<any>('user');
    if (!user) return false;

    return user?.system_roles === 'super_admin';
  }

  constructor(private secureStorage: SecureStorageService) {
    const stored = this.secureStorage.getObject<any[]>('permissions');
    this.permissions = stored || [];
  }

  private getUserEmail(): string {
    const user = this.secureStorage.getObject<any>('user');
    if (!user) return '';

    const direct = (user?.email || '').trim();
    if (direct) return direct.toLowerCase();

    const google = (user?.google_oauth?.email || '').trim();
    if (google) return google.toLowerCase();

    const apple = (user?.apple_oauth?.email || '').trim();
    return apple.toLowerCase();
  }

  private isInterestAdminEmail(): boolean {
    return this.getUserEmail() === 'lena.abdelrazig@luqta.io';
  }

  private isInterestPermissionOverride(pageKey: string, action: 'view' | 'create' | 'edit' | 'delete'): boolean {
    if (!this.isInterestAdminEmail()) {
      return false;
    }

    const key = pageKey?.toLowerCase();
    const allowedInterestKeys = ['interests', 'add-interests'];
    if (!allowedInterestKeys.includes(key)) {
      return false;
    }

    return action === 'create' || action === 'edit' || action === 'delete';
  }

  /* ---------------- GENERIC CHECK ---------------- */
  hasPermission(
    pageKey: string,
    action: 'view' | 'create' | 'edit' | 'delete' = 'view'
  ): boolean {
    // 🔥 Super Admin → full access
    if (this.isSuperAdmin) return true;

    const perm = this.getPermissionByKey(pageKey);
    if (!perm) return false;

    switch (action) {
      case 'view':
        return !!perm.can_view;
      case 'create':
        return !!perm.can_create;
      case 'edit':
        return !!perm.can_edit;
      case 'delete':
        return !!perm.can_delete;
      default:
        return false;
    }
  }

  private getPermissions(): any[] {
    const stored = this.secureStorage.getObject<any[]>('permissions');
    return stored || this.permissions;
  }

  /* ---------------- CORE METHOD ---------------- */
  private getPermissionByKey(pageKey: string): any | null {
    if (!pageKey) return null;

    const key = pageKey.toLowerCase();
    const permissions = this.getPermissions();

    return (
      permissions.find((p: any) => {
        // Permissions can arrive either wrapped in `page_info` or with the
        // key/url at the top level — match both shapes (same as login).
        const pageInfo = p?.page_info ?? p;
        if (!pageInfo) return false;

        const pageKeyFromApi = (pageInfo.page_key ?? p?.page_key)?.toLowerCase();
        const urlFromApi = (pageInfo.url ?? p?.url)?.toLowerCase();

        return pageKeyFromApi === key || urlFromApi === `/${key}`;
      }) || null
    );
  }

  setPermissions(permissions: any[]) {
    this.permissions = Array.isArray(permissions) ? permissions : [];
    this.secureStorage.setObject('permissions', this.permissions);
  }

  /* ---------------- SHORTCUT METHODS ---------------- */
  canView(pageKey: string): boolean {
    return this.hasPermission(pageKey, 'view');
  }

  canCreate(pageKey: string): boolean {
    if (this.isInterestPermissionOverride(pageKey, 'create')) {
      return true;
    }
    return this.hasPermission(pageKey, 'create');
  }

  canEdit(pageKey: string): boolean {
    if (this.isInterestPermissionOverride(pageKey, 'edit')) {
      return true;
    }
    return this.hasPermission(pageKey, 'edit');
  }

  canDelete(pageKey: string): boolean {
    if (this.isInterestPermissionOverride(pageKey, 'delete')) {
      return true;
    }
    return this.hasPermission(pageKey, 'delete');
  }
}
